/* eslint-disable jsx-a11y/media-has-caption */
import classNames from 'classnames'
import React, { useEffect, useState } from 'react'
import { batch, useDispatch, useSelector } from 'react-redux'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import { Button, Tag, TimeCountdown, TimeElapsed } from '@missionlabs/smartagent-app-components'
import { Props as TagProps } from '@missionlabs/smartagent-app-components/dist/Tag'

import useContact from 'hooks/redux/useContact'
import useMonitoring from 'hooks/redux/useMonitoring'
import useContactLogForm from 'hooks/useContactLogForm'
import useSettings from 'hooks/redux/useSettings'
import useTaskConnections from 'hooks/redux/useTaskConnections'
import useACWTimeLeft from 'hooks/redux/useACWTimeLeft'
import { AppConfig } from 'store/app/app.state'
import { extendCallACW, completeCallACW } from 'store/contact/contact.actions'
import { completeTaskACW, extendTaskACW } from 'store/tasks/tasks.actions'
import RootState, { LoadingStates } from 'store/state'
import { setState } from 'store/user/user.actions'
import { reverseBooleanValue, isContactLogEmpty } from 'utils'
import CompleteWrapUp from '../ContactLog/complete-wrap-up'


type TagType = TagProps['type']

interface Props extends RouteComponentProps {
    appConfig: AppConfig
    onTimeout: () => void
    hideWrapUpButton?: boolean
}

const AfterCallWork: React.FC<Props> = ({
    appConfig,
    onTimeout,
    hideWrapUpButton,
}) => {
    const acwTimeLeft = useACWTimeLeft()
    const task = useTaskConnections('selected')
    const voice = useContact()
    const contact = task ?? {...voice, id: voice?.ID}
    const contactLog = useContactLogForm()
    const { settings } = useSettings()
    const channel = contact?.channel ?? contact?.attributes?.['sa-channel'].value
    const { isMonitoringOrBarging, isEnhancedMonitoringEnabled } = useMonitoring()
    const [tagType, setTagType] = useState('dark' as TagType)
    const { parkBay } = useContact() ?? {}
    const nextAgentStatus = useSelector((state: RootState) => state.user?.afterCallStatus)

    const dispatch = useDispatch()

    useEffect(() => {
        if (isEnhancedMonitoringEnabled && isMonitoringOrBarging) {
            contact.id && dispatch(completeCallACW(contact.id))
        }
    }, [isMonitoringOrBarging])

    let audio: HTMLAudioElement | null = null

    const onExtend = () => {
        setTagType('grey')

        switch (channel)
        {
            case 'EMAIL':
                if (task?.id && !task?.acwExtendedAt) {
                    dispatch(extendTaskACW(task.id))
                }
                break
            case 'VOICE':
            case 'QUEUE_CALLBACK':
                if (contact?.id && !contact?.acwExtendedAt) {
                    dispatch(extendCallACW(contact.id))
                }
                break
            default:
                break
        }
    }

    const setTagColour = (time: number) => {
        if (time <= 5) return 'red'
        if (time <= 60) return 'orange'
        if (time <= 120) return 'grey'

        return 'dark'
    }

    const onTimeChange = (time: number) => {
        const newTagType = setTagColour(time)
        if (newTagType !== tagType) setTagType(newTagType)

        if (time === 5 && appConfig.afterCallWorkPlayAlertTone) audio!.play()

        if (time <= 0) {
            switch (channel)
            {
                case 'EMAIL':
                    dispatch(completeTaskACW(task!.id))
                    break
                case 'VOICE':
                case 'QUEUE_CALLBACK':
                    contact.id && dispatch(completeCallACW(contact.id))
                    break
                default:
                    break
            }
            
        }
    }

    const isACWLimited = () => {
        switch (channel) {
            case 'EMAIL':
                return !!settings?.appConfigurations?.task?.isAfterCallWorkLimited 
            case 'VOICE':
            case 'QUEUE_CALLBACK':
                return !!settings?.appConfigurations?.isAfterCallWorkLimited
            default:
                return false
        }
    }

    const canWeExtendACW = () => {
        if (contactLog?.form?.extendWrapTime) {
            return contactLog.form.extendWrapTime
        }
        const ALLOW_AFTER_CONTACT_WORK_EXTENSION = true

        const allowEmailAfterCallWorkExtension = 
                settings?.appConfigurations?.task?.allowAfterCallWorkExtension ??
                reverseBooleanValue(appConfig.hideExtendACW)  ??
                ALLOW_AFTER_CONTACT_WORK_EXTENSION       

        const allowVoiceAfterCallWorkExtension = 
                settings?.appConfigurations?.allowAfterCallWorkExtension ??
                reverseBooleanValue(appConfig.hideExtendACW)  ??
                ALLOW_AFTER_CONTACT_WORK_EXTENSION

        switch (channel) {
            case 'EMAIL':
                return allowEmailAfterCallWorkExtension
            case 'VOICE':
            case 'QUEUE_CALLBACK':
                return allowVoiceAfterCallWorkExtension
            default:
                // Unless it is over-ridden, we can always extend ACW
                return true
        }
    }

    const noContactLogOrEmpty = isContactLogEmpty(contactLog)
    const isContactLogLoading = contactLog?.state === LoadingStates.LOADING
    const showFinishWrapUpButton = !hideWrapUpButton && noContactLogOrEmpty && !isContactLogLoading
    const areWeExtending  = !isACWLimited() || contact?.acwExtendedAt

    return (
        <section className="aftercall row middle between">
            <audio
                ref={(r) => (audio = r)}
                src="https://prod-cdn-12059162352.s3.eu-west-2.amazonaws.com/audio/rollover.mp3"
            />
            <div className="aftercall-timer">
                <div>
                    <p className="title">Wrapping up...</p>
                    {parkBay !== undefined && (
                        <p className="xsm-mar-top">Parked call to slot {parkBay}</p>
                    )}
                </div>
                <div className="aftercall-controls">
                    {canWeExtendACW() && (
                        <Button
                            disabled={!!areWeExtending}
                            onClick={onExtend}
                            className={classNames({
                                'aftercall-controls-extend': true
                            })}
                        >
                            Extend
                        </Button>
                    )}
                    {areWeExtending ? (
                        <Tag small title="wrap up" type={'grey'}>
                            <TimeElapsed ariaLive="off" 
                                date={new Date(contact?.acwExtendedAt || contact?.acwStartedAt || Date.now())}
                            />
                        </Tag>
                    ) : (
                        acwTimeLeft && (
                            <Tag small title="wrap up" type={tagType}>
                                <TimeCountdown
                                    ariaLive="custom"
                                    startTime={acwTimeLeft}
                                    onZero={onTimeout}
                                    onChange={onTimeChange}
                                />
                            </Tag>
                        )
                    )}
                </div>
            </div>
            {showFinishWrapUpButton && (
                <CompleteWrapUp
                    onComplete={() => {
                        // Temporary batch wrap until we've fully migrated to React 18
                        batch(() => {
                            nextAgentStatus && dispatch(setState(nextAgentStatus))
                            if (channel === 'EMAIL') {
                                task?.id && dispatch(completeTaskACW(task.id))
                            } else {
                                contact.id && dispatch(completeCallACW(contact.id))
                            }
                        })
                    }}
                />
            )}
        </section>
    )
}

export default withRouter(AfterCallWork)
